import i from 'i18next'
import { useState, createContext, useEffect } from 'react'
import { ScrollerMotion } from 'scroller-motion'
import { motion } from 'framer-motion'
import useMedia from 'use-media'
import CookieConsent from 'react-cookie-consent'
import Head from 'next/head'
import { MenuCollection, MenuLink } from 'lib/hooks/use-collections'
import useCart, { Cart, LineItem } from 'lib/hooks/use-cart'
import { mobileMenuMaxWidth } from 'constants/styles'
import Nav from './Nav'
import Shipping from './Shipping'
import ShippingFlyout from './flyouts/Shipping'
import CollectionsFlyout from './flyouts/Collections'
import CartFlyout from './flyouts/Cart'
import { Def as StarDef } from './svg/Star'
import GTM, { gtmPush } from './analytics/GTM'
import Script from 'next/script'
import { Converge } from './analytics/Converge'
import TripleWhale, { TriplePixel } from './analytics/TripleWhale'

export type LayoutState = {
	flyout: 'menu'|'collections'|'shipping'|'cart'|false
	setFlyout: ( flyout: 'menu'|'collections'|'shipping'|'cart'|false ) => void
	cart: Cart
	addToCart: ( variant: LineItem['variant'], quantity?: number ) => Promise<any>
	update: ( lineItemId: string, quantity: number ) => Promise<any>
	gift: ( which: 'first'|'second', variantId: string ) => void
	addNote: ( note: string ) => Promise<void>
}
export const State = createContext<LayoutState>( null )

type Props = {
	loaded: boolean
	nav: MenuLink[]
	collections: MenuCollection[]
	back?: string
	children: any
}

export default function Layout({ loaded, nav, collections, children }: Props ) {
	const [ flyout, setFlyout ] = useState<LayoutState[ 'flyout' ]>( false )
	const { cart, add, note: addNote, update, gift } = useCart()
	const addToCart: LayoutState['addToCart'] = ( variant, quantity ) => {
		setFlyout( 'cart' )

		if ( variant.product.id ) {
			const numericProductId = variant.product.id.substring( variant.product.id.lastIndexOf( '/' ) + 1 )
			gtmPush({
				event: 'addToCart',
				ecommerce: {
					detail: {
						products: [{
							name: variant.product.title,
							id: numericProductId,
							price: variant.priceV2.amount,
							brand: 'Ray',
							variant: variant.selectedOptions.map( o => o.value ).join( ' / ' ),
						}],
					},
				},
			})

			const token = cart.id.substring( cart.id.lastIndexOf( '/' ) + 1 )
			TriplePixel( 'AddToCart', { item: numericProductId, q: quantity ?? 1, token })
		}

		return add( variant, quantity )
	}

	const mobile = useMedia({ maxWidth: `${mobileMenuMaxWidth}px` })
	const state = { loaded, flyout, setFlyout, cart, addToCart, addNote, update, gift }

	const [ navigating, setNavigating ] = useState<boolean>( true )
	useEffect( () => {
		window.scrollTo({ top: 0 })
		setTimeout( () => setNavigating( false ), 700 )
	}, [] )

	return (
		<State.Provider value={state}>
			<Head>
				<link rel="icon" href="/favicon.ico" />
				<meta property="og:site_name" content={i.t( 'og:site_name' )} />
			</Head>

			<Script
				src="//static.klaviyo.com/onsite/js/klaviyo.js?company_id=JMm8FK"
			/>
			<Converge />
			<TripleWhale />
			<GTM />
			<StarDef />

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: .5, delay: 3.5 }}
				style={{ position: 'relative', zIndex: 999 }}
			>
				<CookieConsent
					buttonText={i.t( 'cookie.ok' )}
					location="none"
					disableStyles
					acceptOnScroll
				>
					{i.t( 'cookie.text' )}
				</CookieConsent>
			</motion.div>

			<ScrollerMotion
				disabled={mobile || navigating}
				spring={{ mass: .15 }}
			>
				<Shipping
					loaded={loaded}
					onClick={() => {
						setFlyout( f => f === 'shipping' ? false : 'shipping' )
					}}
				/>
				<Nav links={nav} loaded={loaded} />

				<motion.div exit={{ opacity: 0, transition: { duration: .2 } }}>
					{children}
				</motion.div>
			</ScrollerMotion>
			<>
				<ShippingFlyout
					active={flyout === 'shipping'}
					onClose={() => setFlyout( false )}
				/>
				<CartFlyout
					active={flyout === 'cart'}
					onClose={() => setFlyout( false )}
				/>
				<CollectionsFlyout collections={collections} />
			</>
		</State.Provider>
	)
}
